import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import {
  Sparkles, AlertTriangle, CheckCircle, Crown, CreditCard, Bell, BellOff,
  Copy, Share2, MessageCircle, Loader2, Lock, Eye, EyeOff, Gift, Ticket, AlertCircle,
} from 'lucide-react';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';

export function WhatsNewLaunchBanner({ setActiveTab }) {
  const LAUNCH_ID = 'patient-protected-chat-2026-02';
  const STORAGE_KEY = `whatsnew_dismiss_${LAUNCH_ID}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* private mode */ }
    setDismissed(true);
  };
  const shareText = "New on DocSite — your patient chat is now spam-proof. Mobile OTP for new chats (skipped for your existing patients), separate Chat Inbox for leads, set your own per-patient chat caps. https://docsite.care/whats-new/patient-protected-chat";
  return (
    <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-4 md:p-5 mb-5 flex items-start gap-3" data-testid="whatsnew-launch-banner">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-600 to-emerald-600 text-white flex items-center justify-center shrink-0">
        <Sparkles size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold text-sky-700 uppercase tracking-widest">New on DocSite · Just Launched</p>
        <p className="text-sm md:text-base font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Your patient chat is now spam-proof — and brings you real leads
        </p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          Every new chat now starts with a mobile verification — <b>your existing patients skip it automatically</b>. New leads land in the brand-new <b>Chat Inbox</b> tab (right next to Patient Log); your existing patients' chats merge into their Patient Log timeline. Set your own per-patient chat caps in Calls &amp; Chat.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={() => { try { setActiveTab && setActiveTab('chat_inbox'); } catch { /* no-op */ } window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 underline underline-offset-2"
            data-testid="whatsnew-launch-opentab">
            Open Chat Inbox →
          </button>
          <a href="/whats-new/patient-protected-chat" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 underline underline-offset-2"
            data-testid="whatsnew-launch-readmore">
            Read the 1-pager →
          </a>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
            data-testid="whatsnew-launch-share">
            Tell a colleague
          </a>
        </div>
      </div>
      <button onClick={dismiss} aria-label="Dismiss launch banner" data-testid="whatsnew-launch-dismiss"
        className="text-slate-400 hover:text-slate-700 shrink-0 text-lg leading-none px-1">
        ×
      </button>
    </div>
  );
}

export function SetupBlocker({ setActiveTab }) {
  // Hard nudge that sits at the very top of the dashboard until every
  // gating profile item is filled in. Until then, the doctor's `/dr/:slug`
  // page is `noindex` and they're filtered out of `/find/...` listings.
  // Each row deep-links to the tab where that item is fixed.
  //
  // Bonus: when the doctor finishes the LAST blocking item in this session,
  // we fire a confetti celebration + share-tray modal — turns the unlock
  // moment into a viral share opportunity while the dopamine is fresh.
  const [data, setData] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const wasIncompleteRef = useRef(null);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/dashboard/score');
        const next = r.data;
        // Detect transition: previously had blocking items in this session,
        // now has zero, AND backend hasn't celebrated this account yet.
        const hadBlockingBefore = wasIncompleteRef.current === true;
        const isPublishableNow = (next.blocking || []).length === 0;
        if (hadBlockingBefore && isPublishableNow && !next.published_celebrated_at) {
          setShowCelebration(true);
          api.post('/dashboard/profile-publish-celebrated').catch(() => {});
        }
        if (wasIncompleteRef.current === null) {
          wasIncompleteRef.current = (next.blocking || []).length > 0;
        } else if (!isPublishableNow) {
          wasIncompleteRef.current = true;
        }
        setData(next);
      } catch (e) { /* ignore */ }
    };
    load();
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  if (!data) return null;
  const blocking = (data.checks || []).filter(c => c.required && !c.done);

  return (
    <>
      {showCelebration && data.slug && (
        <ProfilePublishedCelebration
          slug={data.slug}
          doctorName={data.doctor_name || ''}
          onClose={() => setShowCelebration(false)}
        />
      )}
      {blocking.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-rose-50 p-5 mb-5" data-testid="setup-blocker">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Profile setup pending</p>
              <p className="text-sm md:text-base font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your website isn't visible to patients yet
              </p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Until you finish the {blocking.length} item{blocking.length === 1 ? '' : 's'} below, your DocSite page is hidden from Google and from the city directory. Patients searching for you in your specialty + city won't see you. Tick these off and you go live instantly.
              </p>
              {data.voice_published_count > 0 && data.voice_published_avg_pct < 40 && (
                <p className="text-[11px] text-violet-700 mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-violet-50 border border-violet-200" data-testid="setup-voice-chip">
                  <Sparkles size={11} /> Your voice: {data.voice_published_avg_pct}% of 40% — keep editing the AI drafts
                </p>
              )}
              <ul className="mt-3 space-y-1.5">
                {blocking.map(c => (
                  <li key={c.key}>
                    <button
                      onClick={() => setActiveTab(c.tab || 'profile')}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-amber-200 hover:bg-amber-50 transition-colors"
                      data-testid={`setup-blocker-${c.key}`}
                    >
                      <span className="w-5 h-5 rounded-full border-2 border-amber-400 shrink-0" />
                      <span className="flex-1 text-sm text-slate-800 font-medium">{c.label}</span>
                      <span className="text-xs text-amber-700 font-semibold">Fix →</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-500 mt-3">
                Tip: open the <span className="font-semibold">Procedures</span> tab → tap <span className="font-semibold">"Auto-fill from catalog"</span> to load 30+ ready procedures with prices in one click. Then tweak only the few you actually offer.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function WebsiteScoreWidget() {
  const [data, setData] = useState(null);
  const [liveExpanded, setLiveExpanded] = useState(false);
  useEffect(() => { api.get('/dashboard/score').then(r => setData(r.data)).catch(() => {}); }, []);
  // When the doctor taps the "Website live" chip, expand the full banner for
  // 5 seconds, then auto-collapse back to the compact chip.
  useEffect(() => {
    if (!liveExpanded) return;
    const t = setTimeout(() => setLiveExpanded(false), 5000);
    return () => clearTimeout(t);
  }, [liveExpanded]);
  if (!data) return null;

  const { score, checks } = data;
  const pct = score;
  const done = checks.filter(c => c.done);
  const todo = checks.filter(c => !c.done);
  // Anything left as `required: true` blocks publish. Optional items still
  // count toward the score but don't gate the live website.
  const blocking = todo.filter(c => c.required !== false);
  const isBlocked = blocking.length > 0;
  const color = pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-500' : 'text-red-500';
  const ringColor = pct >= 80 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
  const circumference = 2 * Math.PI * 38;
  const dashOffset = circumference - (pct / 100) * circumference;

  if (pct === 100) return (
    <div className="mb-6 hidden lg:flex justify-center" data-testid="score-widget-complete">
      {liveExpanded ? (
        <div className="w-full bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-4 flex items-center gap-3 transition-all" data-testid="website-live-banner">
          <CheckCircle size={20} className="text-emerald-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Your website is LIVE</p>
            <p className="text-xs text-emerald-600">All Publish Gate items cleared — your /dr/ URL is now public and indexable.</p>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setLiveExpanded(true)}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          title="Tap to see details"
          data-testid="website-live-chip"
        >
          <CheckCircle size={16} className="text-emerald-600" /> Website live
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="score-widget">
      {/* Hard-block banner — publish gate not yet cleared */}
      {isBlocked && (
        <div className="-mt-1 mb-4 -mx-1 rounded-xl border-2 border-rose-300 bg-rose-50 p-3 flex items-start gap-2.5" data-testid="publish-gate-block-banner">
          <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-bold text-rose-800 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Your website is NOT live yet</p>
            <p className="text-[11px] text-rose-700 mt-1 leading-relaxed">
              Patients visiting your <b>/dr/</b> link will see a "Profile under construction" page until you finish <b>{blocking.length}</b> required item{blocking.length > 1 ? 's' : ''} below. DocSite never publishes thin or half-filled profiles — this protects your reputation and ours.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-5 items-start">
        {/* Score Ring */}
        <div className="flex-shrink-0 relative w-24 h-24">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 84 84">
            <circle cx="42" cy="42" r="38" fill="none" stroke="#E2E8F0" strokeWidth="6" />
            <circle cx="42" cy="42" r="38" fill="none" stroke={ringColor} strokeWidth="6"
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-2xl font-bold ${color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{pct}</span>
            <span className="text-[8px] text-slate-400 -mt-0.5">/ 100</span>
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Publish Gate · Website Readiness</h4>
            {isBlocked
              ? <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider" data-testid="publish-gate-status-blocked">Not live</span>
              : <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid="publish-gate-status-live">Live</span>}
          </div>

          {/* Completed */}
          {done.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {done.map(c => (
                <span key={c.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium" data-testid={`score-done-${c.key}`}>
                  <CheckCircle size={10} /> {c.label}
                </span>
              ))}
            </div>
          )}

          {/* To do — required items shown first with a red lock; optional below */}
          {todo.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                {isBlocked ? 'Required to publish:' : 'Optional — boost your score:'}
              </p>
              {todo.map(c => {
                const req = c.required !== false;
                return (
                  <div key={c.key} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`score-todo-${c.key}`}>
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${req ? 'border-rose-400 bg-rose-50' : 'border-slate-300'}`} />
                    <span className={req ? 'text-rose-800 font-medium' : ''}>{c.label}</span>
                    {req
                      ? <span className="text-[9px] font-bold uppercase tracking-wider text-rose-600 px-1.5 py-0.5 rounded bg-rose-100">Required</span>
                      : <span className="text-[10px] text-sky-500 font-medium">+{c.points} pts</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function TierBadgeWithUpgrade({ tier }) {
  const t = (tier || 'free').toLowerCase();
  if (t === 'turbo') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white text-xs font-bold shadow-sm whitespace-nowrap" data-testid="tier-badge-turbo">
        <Sparkles size={12} /> <span className="lg:hidden">Turbo</span><span className="hidden lg:inline">TurboCharge</span>
      </span>
    );
  }
  if (t === 'premium') {
    return (
      <Link to="/upgrade"
        className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-gradient-to-r from-sky-100 to-fuchsia-100 border border-sky-200 hover:border-fuchsia-300 transition-colors group"
        data-testid="tier-badge-premium-upgrade">
        <Crown size={12} className="text-amber-500" />
        <span className="text-xs font-bold text-slate-800">Coffee Premium</span>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-fuchsia-600 to-rose-500 text-white text-[10px] font-bold uppercase tracking-wider group-hover:scale-105 transition-transform">
          <Sparkles size={10} /> Upgrade to Turbo
        </span>
      </Link>
    );
  }
  // Free / Gold tier
  return (
    <Link to="/upgrade"
      className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 hover:border-amber-300 transition-colors group"
      data-testid="tier-badge-free-upgrade">
      <Crown size={12} className="text-amber-500" />
      <span className="text-xs font-bold text-slate-800">Gold (Free)</span>
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-sky-600 to-sky-500 text-white text-[10px] font-bold uppercase tracking-wider group-hover:scale-105 transition-transform">
        <Sparkles size={10} /> Upgrade
      </span>
    </Link>
  );
}

export function UpgradeBanner() {
  return (
    <Link to="/upgrade" className="block mb-6" data-testid="upgrade-banner">
      <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 rounded-2xl border border-amber-200 p-5 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
          <Crown size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Upgrade to Premium</p>
          <p className="text-xs text-amber-700 mt-0.5">Unlock AI procedure descriptions, case studies, encrypted data export & more. Starting at Rs. 299/month.</p>
        </div>
        <CreditCard size={18} className="text-amber-500 flex-shrink-0" />
      </div>
    </Link>
  );
}

export function SubscriptionWarning() {
  const [state, setState] = useState(null);
  useEffect(() => {
    api.get('/subscriptions/status').then(r => setState(r.data)).catch(() => {});
  }, []);
  if (!state) return null;
  const { warning, in_grace, is_expired, days_left, tier_label, message } = state;

  // Hard expiry/downgrade notice (only shown once right after auto-downgrade)
  if (is_expired && message) {
    return (
      <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 mb-6 flex items-center gap-3" data-testid="subscription-expired-banner">
        <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-amber-800 font-medium">{message}</p>
          <p className="text-xs text-amber-700 mt-0.5">Your published content is still live. Renew anytime to reactivate AI articles, Clinic Kit, and procedure pages.</p>
        </div>
        <Link to="/upgrade" className="px-4 py-1.5 rounded-full bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 flex-shrink-0" data-testid="renew-link">
          Reactivate
        </Link>
      </div>
    );
  }

  if (!warning) return null;

  const isGrace = !!in_grace;
  const barColor = isGrace ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
  const iconColor = isGrace ? 'text-red-500' : 'text-amber-500';
  const textColor = isGrace ? 'text-red-800' : 'text-amber-800';
  const btnColor = isGrace ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700';

  return (
    <div className={`rounded-2xl border p-4 mb-6 flex items-center gap-3 ${barColor}`} data-testid="subscription-warning">
      <AlertTriangle size={18} className={`${iconColor} flex-shrink-0`} />
      <div className="flex-1">
        <p className={`text-sm font-medium ${textColor}`}>{warning}</p>
        <p className={`text-xs mt-0.5 ${textColor} opacity-80`}>
          Your current tier: <b>{tier_label}</b>{typeof days_left === 'number' && days_left > 0 ? ` · ${days_left} day${days_left === 1 ? '' : 's'} left` : ''}
        </p>
      </div>
      <Link to="/upgrade" className={`px-4 py-1.5 rounded-full text-white text-xs font-medium flex-shrink-0 ${btnColor}`} data-testid="renew-link">
        Renew Now
      </Link>
    </div>
  );
}

export function PushNotificationToggle() {
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const [loading, setLoading] = useState(false);

  // Auto-subscribe on every dashboard visit whenever there's no live
  // subscription. Two scenarios are covered:
  //   1. Permission === 'default' (never asked): we ask + subscribe.
  //   2. Permission === 'granted' but subscription is missing (typical after
  //      a PWA uninstall+reinstall — browser remembers the permission grant
  //      but the new SW has no PushManager subscription yet). We silently
  //      re-subscribe — no prompt, no doctor action needed. This is the
  //      auto-heal that keeps push notifications working without forcing
  //      non-tech doctors to find a hidden Enable button.
  useEffect(() => {
    if (!isSupported || isSubscribed) return;
    if (permission === 'denied') return;
    // Silent re-subscribe — no UX flash because permission is already granted
    // (or default, in which case the browser shows its own prompt once).
    subscribe().catch(() => {});
  }, [isSupported, isSubscribed, permission, subscribe]);

  if (!isSupported) return null;

  const handleToggle = async () => {
    setLoading(true);
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
    setLoading(false);
  };

  return (
    <div className="bg-gradient-to-r from-sky-50 to-violet-50 rounded-xl border border-sky-200 p-4 flex items-center justify-between gap-4" data-testid="push-toggle">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSubscribed ? 'bg-emerald-100' : 'bg-slate-100'}`}>
          {isSubscribed ? <Bell size={16} className="text-emerald-600" /> : <BellOff size={16} className="text-slate-400" />}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">Smart Login Notifications</p>
          <p className="text-[10px] text-slate-500">
            {isSubscribed
              ? 'Enabled — subsequent logins today use free push notifications instead of SMS'
              : permission === 'denied'
                ? 'Blocked — enable notifications in your browser settings'
                : 'Enable to receive login codes via notification (saves SMS costs)'}
          </p>
        </div>
      </div>
      <button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        className={`px-4 py-2 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
          isSubscribed
            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            : 'bg-sky-600 text-white hover:bg-sky-700'
        } disabled:opacity-50`}
        data-testid="push-toggle-btn"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : isSubscribed ? <BellOff size={12} /> : <Bell size={12} />}
        {isSubscribed ? 'Disable' : 'Enable'}
      </button>
    </div>
  );
}

export function ShareUrlBar({ slug }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${window.location.origin}/dr/${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`Check out my doctor profile: ${fullUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'My Doctor Website', url: fullUrl }); } catch {}
    } else { handleCopy(); }
  };

  return (
    <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-2" data-testid="share-url-bar">
      <p className="text-xs font-medium text-sky-800 uppercase tracking-wide">Your Website URL</p>
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-white border border-sky-200 rounded-lg px-3 py-2 text-sm font-mono text-sky-700 truncate" data-testid="share-url-display">
          {fullUrl}
        </div>
        <button onClick={handleCopy} data-testid="copy-url-btn"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${copied ? 'bg-emerald-600 text-white' : 'bg-sky-600 text-white hover:bg-sky-700'}`}>
          {copied ? <><CheckCircle size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
        <button onClick={handleWhatsApp} data-testid="share-whatsapp-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors">
          <MessageCircle size={14} /> WhatsApp
        </button>
        <button onClick={handleShare} data-testid="share-general-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 transition-colors">
          <Share2 size={14} /> Share
        </button>
      </div>
    </div>
  );
}

export function RedeemCouponCard({ onRedeemed, tier }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Hide the entire "Have an access code?" card for doctors who are already
  // on the top tier — there's no further upgrade their coupon can buy.
  // Must be AFTER all useState calls to respect Rules of Hooks.
  if (tier === 'turbo') return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true); setMsg(''); setErr('');
    try {
      const { data } = await api.post('/dashboard/redeem-coupon', { code: code.trim() });
      setMsg(data.message || `Access activated: ${data.tier} for ${data.months_granted} months`);
      setCode('');
      onRedeemed && onRedeemed(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Could not redeem this code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 via-rose-50 to-fuchsia-50 border border-amber-200 rounded-2xl p-4 md:p-5" data-testid="redeem-coupon-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-white border border-amber-200 flex items-center justify-center shrink-0">
            <Gift size={18} className="text-amber-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Have an access code?</p>
            <p className="text-xs text-slate-600 truncate">Redeem a DocSite privileged-access code to unlock premium features.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(o => !o); setErr(''); setMsg(''); }}
          className="shrink-0 rounded-full px-3.5 py-1.5 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors flex items-center gap-1.5"
          data-testid="redeem-coupon-toggle"
        >
          <Ticket size={12} /> {open ? 'Close' : 'Redeem'}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="redeem-coupon-form">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="Enter code (e.g. CHETNA-PREMIUM-2026)"
            className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none tracking-wider font-mono"
            data-testid="redeem-coupon-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="rounded-xl px-5 py-2.5 bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="redeem-coupon-submit"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Redeem
          </button>
        </form>
      )}

      {msg && <p className="mt-3 text-sm text-emerald-700 font-medium flex items-center gap-1.5" data-testid="redeem-coupon-success"><CheckCircle size={14} /> {msg}</p>}
      {err && <p className="mt-3 text-sm text-red-600 font-medium flex items-center gap-1.5" data-testid="redeem-coupon-error"><AlertCircle size={14} /> {err}</p>}
    </div>
  );
}

export function VoiceAuthenticityNotice() {
  return (
    <div
      className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-rose-50 p-5"
      data-testid="voice-authenticity-notice"
      role="note"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">
            Important · Personalise before publishing
          </p>
          <p className="text-sm md:text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            If you don't add your own voice to the AI drafts, your website won't rank — and patients won't find you.
          </p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Google now actively down-ranks unedited AI text. Until at least <strong className="text-slate-900">40% of one section</strong> reads in your own words —
            your specialty, your city, your patient stories — your profile is held back from search results, the city directory, and the patient discovery feed.
            That means <strong className="text-rose-700">no visibility, no leads, no bookings</strong>.
          </p>
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong>What to do:</strong> Edit each section below. Add one local detail (e.g. your clinic neighbourhood),
            one personal line about why you became a doctor, and one specific procedure or condition you treat most. The Voice meter shows you exactly when you've hit 40%.
          </p>
        </div>
      </div>
    </div>
  );
}

export function ChangePasswordSection() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleChange = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setMsg('Passwords do not match'); return; }
    setLoading(true); setMsg('');
    try {
      const { data } = await api.post('/auth/change-password', { old_password: oldPw, new_password: newPw });
      setMsg(data.message);
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { setMsg(err.response?.data?.detail || 'Error changing password'); }
    setLoading(false);
  };

  return (
    <div className="mt-8 pt-6 border-t border-slate-200" data-testid="change-password-section">
      <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Lock size={14} className="text-sky-600" /> Change Password
      </h3>
      <form onSubmit={handleChange} className="space-y-3 max-w-sm">
        <div className="relative">
          <input type={showOld ? 'text' : 'password'} value={oldPw} onChange={e => setOldPw(e.target.value)}
            placeholder="Current password" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
            data-testid="doc-old-password" />
          <button type="button" onClick={() => setShowOld(!showOld)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
            {showOld ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <div className="relative">
          <input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
            placeholder="New password (min 8, 1 uppercase, 1 number)" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
            data-testid="doc-new-password" />
          <button type="button" onClick={() => setShowNew(!showNew)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
            {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
          placeholder="Confirm new password" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
          data-testid="doc-confirm-password" />
        {msg && <p className={`text-xs ${msg.includes('success') ? 'text-emerald-600' : 'text-red-600'}`}>{msg}</p>}
        <button type="submit" disabled={loading}
          className="px-5 py-2 rounded-xl bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5"
          data-testid="doc-change-password-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Change Password
        </button>
      </form>
    </div>
  );
}

export function Field({ label, value, onChange, placeholder, type = 'text', testid, onBlur }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</label>
      <input type={type} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
        value={value || ''} onChange={e => onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

